<template>
  <v-container fluid class="pa-2 pa-md-4 grey lighten-5 min-h-screen">
    
    <!-- HEADER Y FILTROS -->
    <v-card class="rounded-xl pa-2 mb-4 elevation-2 border-light white">
      <v-row align="center" no-gutters class="pa-1">
        <v-col cols="12" md="4" class="px-2">
          <v-text-field
            v-model="searchQuery"
            prepend-inner-icon="mdi-magnify"
            label="Buscar activo..."
            outlined dense hide-details
            flat background-color="grey lighten-4"
            class="rounded-lg"
          ></v-text-field>
        </v-col>
        
        <v-col cols="12" md="4" class="px-2">
          <v-select
            v-model="filterType"
            :items="typeOptions"
            item-text="title"
            item-value="value"
            label="Tipo de trabajo"
            outlined dense hide-details
            background-color="grey lighten-4"
            prepend-inner-icon="mdi-filter-variant"
            class="rounded-lg"
          ></v-select>
        </v-col>

        <v-spacer></v-spacer>

        <v-col cols="12" md="2" class="px-2">
          <v-select
            v-model="selectedYear"
            :items="yearItems"
            outlined dense hide-details
            prepend-inner-icon="mdi-calendar-multiselect"
            background-color="blue lighten-5"
            color="primary"
            class="rounded-lg font-weight-bold"
            @change="loadHistory"
          ></v-select>
        </v-col>
      </v-row>
    </v-card>

    <!-- TABLA MATRIX CON RESALTADO DE COLUMNA -->
    <v-card class="rounded-xl elevation-3 overflow-hidden border-main">
      <div class="overflow-auto custom-scroll table-container">
        <table class="history-table">
          <thead>
            <tr class="month-header">
              <th class="sticky-column corner-cell bg-grey-light">Meses</th>
              <!-- COLSPAN DINÁMICO SEGÚN EL CÁLCULO -->
              <th 
                v-for="m in monthsLayout" 
                :key="m.name" 
                :colspan="m.colspan" 
                class="month-label"
              >
                {{ m.name }}
              </th>
            </tr>
            <tr>
              <th class="sticky-column shadow-right corner-cell">Máquina</th>
              <!-- SEMANAS DINÁMICAS (52 o 53) -->
              <th 
                v-for="w in totalWeeksInYear" 
                :key="w" 
                class="week-col header-cell"
                :class="{ 
                  'current-week-head': isCurrentWeek(w),
                  'month-divider': isEndOfMonth(w) 
                }"
              >
                {{ w }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="maquina in historicoFiltrado" :key="maquina.id" class="row-hover">
              <td class="sticky-column machine-cell font-weight-bold shadow-right">
                {{ maquina.nombre }}
              </td>
              <td 
                v-for="w in totalWeeksInYear" 
                :key="w" 
                class="border-cell"
                :class="{ 
                  'current-week-column': isCurrentWeek(w),
                  'month-divider': isEndOfMonth(w),
                }"
              >
                <div class="grid-cell-container">
                  <template v-for="trabajo in getTrabajosSemana(maquina.id, w)">
                    <v-tooltip bottom open-delay="50" :key="trabajo.id" content-class="custom-tooltip-style">
                      <template v-slot:activator="{ on, attrs }">
                        <button
                          v-on="on" v-bind="attrs"
                          class="mini-btn-modern"
                          :style="{ backgroundColor: getColorHex(trabajo) }"
                          @click="verDetalle(trabajo)"
                        >
                          <v-icon size="10" color="white">{{ getIconTrabajo(trabajo) }}</v-icon>
                        </button>
                      </template>
                      <div class="pa-1 text-center">
                        <div class="font-weight-bold mb-1">{{ getNombreTrabajo(trabajo) }}</div>
                        <div class="caption">{{ formatDate(trabajo.fecha) }}</div>
                      </div>
                    </v-tooltip>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-card>

    <!-- DIALOGO DE DETALLE -->
    <v-dialog v-model="detalleDialog" max-width="650">
      <v-card v-if="trabajoSeleccionado" class="rounded-xl">
        <v-toolbar :color="getColorTrabajo(trabajoSeleccionado)" dark flat dense>
          <v-toolbar-title class="subtitle-1 font-weight-bold">
            {{ trabajoSeleccionado.tipo === 'correctivo' ? 'Editar Correctivo' : getNombreTrabajo(trabajoSeleccionado)
            }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="detalleDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>

        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12" class="pb-0">
              <div class="overline grey--text">Máquina</div>
              <div class="headline">
                {{ trabajoSeleccionado.maquina_nombre || trabajoSeleccionado.nombre_maquina || 'Máquina desconocida' }}
              </div>
            </v-col>
          </v-row>

          <!-- SECCIÓN PARA CORRECTIVOS -->
          <template v-if="trabajoSeleccionado.tipo === 'correctivo'">
            <v-divider class="my-4"></v-divider>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="trabajoSeleccionado.fecha_programada" label="Fecha Programada" type="date"
                  outlined dense></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="trabajoSeleccionado.empresa" :items="clientes" item-text="nombre" item-value="nombre"
                  label="Empresa / Técnico" outlined dense prepend-inner-icon="mdi-account-hard-hat"
                  clearable></v-select>
              </v-col>

              <v-col cols="6" md="4">
                <v-text-field v-model.number="trabajoSeleccionado.tiempo_min" label="Tiempo (min)" type="number"
                  outlined dense suffix="min"></v-text-field>
              </v-col>
              <v-col cols="6" md="4">
                <v-text-field v-model.number="trabajoSeleccionado.coste" label="Coste (€)" type="number" outlined dense
                  suffix="€"></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-switch v-model="trabajoSeleccionado.parada_produccion" label="Parada Prod." color="error" dense
                  hide-details></v-switch>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>
            <div class="subtitle-1 font-weight-bold mb-2">Tareas de Reparación</div>

            <v-list dense class="grey lighten-4 rounded-lg">
              <v-list-item v-for="(t, i) in trabajoSeleccionado.tareas" :key="i">
                <v-list-item-action>
                  <v-checkbox v-model="t.estado" color="success"></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title :class="t.estado ? 'text-decoration-line-through grey--text' : ''">
                    {{ t.descripcion }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>

            <v-card-actions class="px-0 mt-4">
              <v-btn color="success" block large depressed :loading="isSaving" @click="guardarCambiosCorrectivo">
                <v-icon left>mdi-content-save</v-icon>
                Guardar Cambios
              </v-btn>
            </v-card-actions>
          </template>

          <!-- VISTA PARA PREVENTIVOS -->
          <template v-else>
            <v-row>
              <v-col cols="6">
                <div class="overline grey--text">Fecha</div>
                <div class="body-1">{{ formatDate(trabajoSeleccionado.fecha) }}</div>
              </v-col>
              <v-col cols="6">
                <div class="overline grey--text">Estado</div>
                <v-chip small :color="getColorTrabajo(trabajoSeleccionado)" dark>
                  {{ trabajoSeleccionado.estado }}
                </v-chip>
              </v-col>
            </v-row>
            <v-divider class="my-4"></v-divider>
            <div class="subtitle-1 font-weight-bold mb-2">Tareas</div>
            <v-list dense class="grey lighten-4 rounded-lg">
              <v-list-item v-for="(t, i) in trabajoSeleccionado.tareas" :key="i">
                <v-list-item-icon>
                  <v-icon :color="isTareaFinalizada(t) ? 'success' : 'error'">
                    {{ isTareaFinalizada(t) ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ t.descripcion }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { jwtDecode } from "jwt-decode";
//   import ExcelJS from 'exceljs'
//   import { saveAs } from 'file-saver'

export default {
  name: 'HistoricoMantenimiento',
  data() {
    return {
      selectedYear: new Date().getFullYear(),
      historico: [],
      detalleDialog: false,
      trabajoSeleccionado: null,
      isSaving: false,
      clientes: [],
      baseYear: 2026,
      currentYear: new Date().getFullYear(),
      searchQuery: '',
      filterType: 'todos',
      typeOptions: [
        { title: 'Todos los trabajos', value: 'todos' },
        { title: 'Preventivo', value: 'preventivo' },
        { title: 'Correctivo', value: 'correctivo' },
        { title: 'Estimado', value: 'estimado' },
      ],

      meses: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    }
  },
  computed: {
    yearItems() {
      const years = []
      for (let y = this.baseYear; y <= this.currentYear; y++) {
        years.push(y)
      }
      return years
    },
    historicoFiltrado() {
      let resultado = [...this.historico];
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        resultado = resultado.filter(m => m.nombre.toLowerCase().includes(query));
      }
      if (this.filterType !== 'todos') {
        resultado = resultado.map(maquina => {
          const trabajosFiltrados = maquina.trabajos.filter(t => {
            if (this.filterType === 'correctivo') return t.tipo === 'correctivo';
            if (this.filterType === 'estimado') return t.tipo === 'estimado';
            if (this.filterType === 'preventivo') return t.tipo !== 'correctivo' && t.tipo !== 'estimado';
            return true;
          });
          return { ...maquina, trabajos: trabajosFiltrados };
        }).filter(maquina => maquina.trabajos.length > 0 || this.searchQuery === '');
      }
      return resultado;
    },
    monthsLayout() {
      const year = this.selectedYear;
      const counts = new Array(12).fill(0);

      // Calculamos el total de semanas que tiene este año según ISO
      const totalWeeks = moment(`${year}-12-28`).isoWeek();

      for (let w = 1; w <= totalWeeks; w++) {
        // Buscamos el jueves de esa semana (día ancla de la semana ISO)
        // para saber a qué mes pertenece mayoritariamente esa semana
        const monthIndex = moment().year(year).isoWeek(w).isoWeekday(4).month();
        counts[monthIndex]++;
      }

      return this.meses.map((name, i) => ({
        name,
        colspan: counts[i]
      }));
    },

    totalWeeksInYear() {
      // Devuelve 52 o 53 según el año
      return moment(`${this.selectedYear}-12-28`).isoWeek();
    }
  },
  mounted() {
    this.loadHistory()
  },
  methods: {
    isTareaFinalizada(tarea) {
      return tarea.estado === 'bien' || tarea.estado === true || tarea.estado === 'finalizado';
    },
    getTrabajosSemana(maquinaId, semana) {
      const maquina = this.historicoFiltrado.find(m => m.id === maquinaId)
      if (!maquina) return []
      return maquina.trabajos.filter(t => moment(t.fecha).isoWeek() === semana)
    },
    async loadHistory() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/historico?year=${this.selectedYear}`, {
          params: { id_empresa: jwtDecode(localStorage.getItem("token")).id_cliente }
        });
        this.historico = response.data
      } catch (e) { console.error(e) }
    },
    getColorTrabajo(t) {
      if (t.tipo === 'correctivo') return 'error';
      if (t.estado === 'finalizado') return 'success';
      if (t.estado === 'incompleto') return 'primary';
      if (t.estado === 'abierto') return 'grey';
      if (t.tipo === 'estimado') return 'orange';
      return 'primary';
    },
    isCurrentWeek(w) {
      const isThisYear = this.selectedYear == new Date().getFullYear();
      return isThisYear && w === moment().isoWeek();
    },
    isEndOfMonth(w) {
      let sum = 0;
      // Comprobamos si la semana 'w' es la última de algún mes
      for (const m of this.monthsLayout) {
        sum += m.colspan;
        if (w === sum) return true;
      }
      return false;
    },
    // Asegúrate de que este método devuelva una cadena válida para el color
    getColorHex(t) {
      const colors = {
        'correctivo': '#F44336', // Rojo
        'finalizado': '#4CAF50',  // Verde
        'incompleto': '#2196F3',  // Azul
        'abierto': '#9E9E9E',     // Gris
        'estimado': '#FB8C00'     // Naranja
      };

      if (t.tipo === 'correctivo') return colors.correctivo;
      if (t.tipo === 'estimado') return colors.estimado;
      return colors[t.estado] || colors.incompleto;
    },
    getIconTrabajo(t) {
      if (t.tipo === 'correctivo') return 'mdi-tools';
      if (t.tipo === 'estimado') return 'mdi-calendar-clock';
      if (t.estado === 'finalizado') return 'mdi-check';
      if (t.estado === 'incompleto') return 'mdi-progress-wrench';
      if (t.estado === 'abierto') return 'mdi-clock-outline';
      return 'mdi-help';
    },
    getNombreTrabajo(t) {
      if (t.tipo === 'correctivo') return `${t.descripcion || 'Sin descripción'}`;
      return t.plantilla || t.plantilla_nombre || t.nombre_plantilla || t.nombre || 'Mantenimiento Preventivo';
    },
    formatDate(d) { return moment(d).format('DD/MM/YYYY') },
    verDetalle(t) {
      const copia = JSON.parse(JSON.stringify(t));
      if (copia.fecha_programada) copia.fecha_programada = moment(copia.fecha_programada).format('YYYY-MM-DD');
      if (copia.tipo === 'correctivo' && copia.tareas) {
        copia.tareas = copia.tareas.map(tarea => ({ ...tarea, estado: !!tarea.estado }));
      }
      this.trabajoSeleccionado = copia;
      this.detalleDialog = true;
    },
    async guardarCambiosCorrectivo() {
      this.isSaving = true;
      try {
        const payload = {
          correctivo_id: this.trabajoSeleccionado.id,
          tareas: this.trabajoSeleccionado.tareas,
          fecha_programada: this.trabajoSeleccionado.fecha_programada,
          empresa: this.trabajoSeleccionado.empresa,
          tiempo_min: this.trabajoSeleccionado.tiempo_min,
          coste: this.trabajoSeleccionado.coste,
          parada_produccion: this.trabajoSeleccionado.parada_produccion
        };
        await axios.post(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/correctivos/actualizar`, payload);
        await this.loadHistory();
        this.detalleDialog = false;
      } catch (e) {
        this.$avisar("Error al guardar", "error");
      } finally { this.isSaving = false; }
    }
  }
}
</script>

<style scoped>
.table-container {
  max-height: calc(100vh - 280px);
  position: relative;
  margin: 0;
}

.corner-cell {
  /* Añadimos un padding izquierdo extra para que el texto no toque el borde redondeado */
  padding-left: 20px !important; 
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.75rem !important;
  color: #1976d2 !important; /* Color primario para destacar títulos */
}

.history-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 1850px;
}

/* COLUMNA FIJA */
.sticky-column {
  position: sticky;
  left: 0;
  z-index: 10;
  width: 200px;
  background-color: white !important;
  text-align: left;
}

.shadow-right {
  border-right: 2px solid #1976d2 !important; /* Marca visual fuerte */
  box-shadow: 4px 0 10px rgba(0,0,0,0.05);
}

.machine-cell {
  padding-left: 15px;
  font-size: 0.85rem;
  color: #263238;
}

/* CABECERAS */
.header-cell {
  position: sticky;
  top: 35px;
  z-index: 5;
  background-color: #f8f9fa;
  border-bottom: 2px solid #e0e0e0;
  height: 45px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #546e7a;
}

.month-label {
  position: sticky;
  top: 0;
  z-index: 6;
  background-color: #eceff1;
  color: #455a64;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  border-right: 1px solid #cfd8dc;
  border-bottom: 1px solid #cfd8dc;
  height: 35px;
  text-align: center;
}

.month-header .sticky-column {
  top: 0;
  z-index: 11; /* Por encima de todo en la esquina */
  background-color: #eceff1 !important;
  height: 35px;
  display: flex;
  align-items: center;
}

/* DIVISIONES MENSUALES */
.month-divider {
  border-right: 2px solid #b0bec5 !important;
}

/* RESALTADO SEMANA ACTUAL (TOQUE SUTIL) */
.current-week-head {
  background-color: #bbdefb !important; /* Azul un poco más fuerte para el número */
  color: #0d47a1 !important;
  font-weight: 900 !important;
}

.current-week-column {
  background-color: #f0f7ff !important; /* EL TOQUE SUTIL: Azul muy muy claro */
}

/* INTERACCIÓN (FILA EN AZUL AL PASAR EL RATÓN) */
.row-hover:hover {
  background-color: #e3f2fd !important; /* Azul Vuetify claro */
}

.row-hover:hover .sticky-column {
  background-color: #e3f2fd !important;
}

/* Al pasar el ratón por la celda específica, un refuerzo visual */
.border-cell:hover {
  background-color: #bbdefb !important;
}

/* CELDAS Y BOTONES */
.border-cell {
  border-bottom: 1px solid #eeeeee;
  border-right: 1px solid #f5f5f5;
  height: 52px;
}

.week-col { width: 34px; text-align: center; }

.mini-btn-modern {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.3);
  cursor: pointer;
  transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  /* --- MEJORAS DE CENTRADO --- */
  display: flex;    
  align-items: center;  
  justify-content: center;
  padding: 0;    
  line-height: 0;
  outline: none;
}

.mini-btn-modern .v-icon {
  margin: 0 !important;
  padding: 0 !important;
  display: block;
}

.grid-cell-container {
  display: flex;
  gap: 3px;
  justify-content: center;
  align-items: center;
}

/* SCROLLBAR */
.custom-scroll::-webkit-scrollbar { height: 10px; width: 8px; }
.custom-scroll::-webkit-scrollbar-thumb { background: #b0bec5; border-radius: 10px; }

.rounded-xl { border-radius: 16px !important; }

/* LEYENDA DOTS */
.dot { height: 10px; width: 10px; border-radius: 50%; display: inline-block; margin-right: 6px; }
.dot.success { background-color: #4CAF50; }
.dot.primary { background-color: #2196F3; }
.dot.grey { background-color: #9E9E9E; }
.dot.error { background-color: #F44336; }
.dot.orange { background-color: #FB8C00; }
</style>